import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { validators, formats, dates } from 'investira.sdk';
import { IconButton, Tooltip, Stack, Icon, Typography } from 'investira.react.components';
import { BulkContextActions, DataTable } from '../';
import { MutedCopy } from '../../molecules';
import { CARTEIRA_TIPO } from '../../../enums';
import usePerfil from '../../../hooks/usePerfil';
import useEntidade from '../../../hooks/useEntidade';
import { addWorkingDays, startOf, toDate } from 'investira.sdk/lib/utils/dates';

const PortfoliosDataGrid = props => {
  const {
    state,
    output_id,
    onFavClick,
    onEditClick,
    onBulkDeleteClick,
    onDeleteClick,
    onSwitchChange,
    renderDetailPanel,
    data,
    ...restProps
  } = props;

  const { config } = useEntidade();

  const { usuarioPrioridade } = usePerfil();

  // Estado local para controlar os switches (update otimista)
  const [switchStates, setSwitchStates] = useState({});

  // Sincroniza o estado local com os dados iniciais
  useEffect(() => {
    if (validators.isNull(data) || !Array.isArray(data)) {
      return;
    }

    const xStates = {};

    data.forEach(xRow => {
      const xCarteiraId = xRow.carteira_id;
      const xCarteiraConfig = xRow.carteira_config;
      const xValue =
        xCarteiraConfig === null || xCarteiraConfig === undefined
          ? true
          : xCarteiraConfig?.custodiante_conferencia ?? false;
      xStates[xCarteiraId] = Boolean(xValue);
    });

    setSwitchStates(xStates);
  }, [data]);

  /**
   * Verifica se a data está dentro do período de conferência
   */
  const isValidConferencia = pData => {
    const xDiasDelay = Number(config?.custodiante_conferencia_dias_delay || 0);
    const xDiasJanela = Number(config?.custodiante_conferencia_dias_janela || 0);
    const xAtraso = xDiasDelay + xDiasJanela;
    const xDataInicio = startOf('day', addWorkingDays(toDate(), -xAtraso));
    const xDataFim = addWorkingDays(xDataInicio, xDiasJanela);
    if (toDate(pData) < toDate(xDataInicio) || toDate(pData) > toDate(xDataFim)) {
      return false;
    }
    return true;
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'portfolio',
        header: 'Portfolio',
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: {
            width: '100% !important'
          }
        },
        Cell: ({ cell, row }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return (
            <MutedCopy title="Copiar" value={row.original.carteira_id}>
              {cell.getValue()}
            </MutedCopy>
          );
        }
      },
      {
        accessorKey: 'quantidade_carteiras',
        header: 'Carteiras',
        enableColumnActions: false,
        enableSorting: false,
        muiTableBodyCellProps: {
          sx: {
            justifyContent: 'flex-end',
            textAlign: 'right'
          }
        },
        muiTableHeadCellProps: () => {
          return { align: 'right' };
        },

        Cell: ({ cell, row }) => {
          if (validators.isEmpty(cell.getValue())) {
            return;
          }
          const xCarteiras = row.original.carteiras;
          const xValor = cell.getValue();
          const xRenderCarteiraItem = (pCarteira, pKey) => (
            <Stack key={pKey} direction="column" spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {pCarteira.carteira}
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {pCarteira.carteira_seuid}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {CARTEIRA_TIPO[pCarteira.carteira_tipo_id]?.label}
                </Typography>
              </Stack>
            </Stack>
          );

          if (validators.isEmpty(xValor)) {
            return;
          }

          const xTooltipTitle =
            Array.isArray(xCarteiras) && xCarteiras.length > 0 ? (
              <Stack spacing={1} py={1} px={1}>
                {xCarteiras.length > 5 ? (
                  <Stack direction="row" spacing={2}>
                    {[...Array(2)].map((_, xColIdx) => {
                      const xPerCol = Math.ceil(xCarteiras.length / 2);
                      const xStart = xColIdx * xPerCol;
                      const xEnd = xStart + xPerCol;
                      const xColumnCarteiras = xCarteiras.slice(xStart, xEnd);
                      return (
                        <Stack key={`column-${xColIdx}`} direction="column" spacing={0.5}>
                          {xColumnCarteiras.map((xCarteira, xIdx) =>
                            xRenderCarteiraItem(
                              xCarteira,
                              xCarteira.carteira_id || `col-${xColIdx}-idx-${xIdx}`
                            )
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : (
                  xCarteiras.map((xCarteira, xIdx) =>
                    xRenderCarteiraItem(xCarteira, xCarteira.carteira_id || `idx-${xIdx}`)
                  )
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma carteira encontrada
              </Typography>
            );

          return (
            <Tooltip
              title={xTooltipTitle}
              arrow
              placement="right"
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 'none',
                    width: 'fit-content'
                  }
                }
              }}>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                <span>{xValor}</span>
                <Icon iconName="information" size={16} color="primary" />
              </Stack>
            </Tooltip>
          );
        }
      },
      {
        accessorKey: 'modified',
        header: 'Última Alteração',
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        }
      }
    ],
    [onSwitchChange, switchStates, config?.custodiante_conferencia]
  );

  const xProps = {
    columns,
    data,
    state,
    enableRowSelection: usuarioPrioridade <= 6,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: ''
      }
    },
    renderTopToolbarCustomActions: ({ table }) => {
      if (usuarioPrioridade > 6) {
        return null;
      }

      const selectedRows = table.getSelectedRowModel().rows;
      const isSomeRowSelected = selectedRows.length > 0;

      return (
        <BulkContextActions
          onDeleteClick={() => {
            onBulkDeleteClick(selectedRows);
          }}
          disabled={state.isLoading || state.showProgressBars || !isSomeRowSelected}
        />
      );
    },
    renderRowActions: ({ row }) => {
      return (
        <Stack direction="row" spacing={2}>
          {usuarioPrioridade <= 6 && (
            <Tooltip title="Editar portfolio">
              <IconButton color="primary" onClick={() => onEditClick(row.original)}>
                <Icon iconName="edit" size={21} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      );
    },
    ...restProps
  };

  return <DataTable {...xProps} />;
};

PortfoliosDataGrid.propTypes = {
  data: PropTypes.array.isRequired,
  state: PropTypes.object,
  initialState: PropTypes.object
};

export default PortfoliosDataGrid;
